export type MarketingConsentState = "granted" | "denied" | "unknown";

type MarketingVendorConsentOptions = {
  scriptId: string;
  cookiePrefix: string;
  knownCookieNames: string[];
  sessionStoragePrefix?: string;
  localStoragePrefixes?: string[];
  appendScript: () => void;
};

type CookieTargetWindow = Pick<Window, "location">;

const getCookieDomains = (targetWindow: CookieTargetWindow): string[] => {
  const hostname = targetWindow.location.hostname.replace(/^\./, "");
  const labels = hostname.split(".").filter(Boolean);

  if (labels.length < 2) {
    return hostname ? [hostname] : [];
  }

  return labels.slice(0, -1).map((_, index) => labels.slice(index).join("."));
};

export const clearFirstPartyCookies = (
  targetWindow: CookieTargetWindow,
  targetDocument: Pick<Document, "cookie">,
  cookiePrefixes: readonly string[],
  knownCookieNames: readonly string[] = [],
): void => {
  const visibleCookieNames = targetDocument.cookie
    .split(";")
    .map((cookie) => cookie.split("=", 1)[0]?.trim())
    .filter(
      (name): name is string =>
        Boolean(name) &&
        cookiePrefixes.some((prefix) => name.startsWith(prefix)),
    );

  new Set([...knownCookieNames, ...visibleCookieNames]).forEach((name) => {
    const cookie = `${name}=; Max-Age=0; path=/`;
    targetDocument.cookie = cookie;
    getCookieDomains(targetWindow).forEach((hostname) => {
      targetDocument.cookie = `${cookie}; domain=${hostname}`;
      targetDocument.cookie = `${cookie}; domain=.${hostname}`;
    });
  });
};

export const clearStoragePrefixes = (
  storage: Pick<Storage, "length" | "key" | "removeItem">,
  prefixes: readonly string[],
): void => {
  try {
    Array.from({length: storage.length}, (_, index) => storage.key(index))
      .filter(
        (name): name is string =>
          name !== null && prefixes.some((prefix) => name.startsWith(prefix)),
      )
      .forEach((name) => storage.removeItem(name));
  } catch {
    // Storage access can be blocked independently of Cookiebot consent.
  }
};

type PreservedFormControl = {
  checked?: boolean;
  index: number;
  value?: string;
};

type ConsentReloadHistoryState = Record<string, unknown> & {
  __rhConsentFormState?: PreservedFormControl[];
};

const withdrawalReloadWindows = new WeakSet<Window>();

const FORM_CONTROL_SELECTOR =
  'input:not([type="hidden"]):not([type="password"]):not([type="file"]), textarea, select';

const getEditableFormControls = (
  targetDocument: Document,
): Array<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> =>
  Array.from(
    targetDocument.querySelectorAll<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >(FORM_CONTROL_SELECTOR),
  );

const isCheckableInput = (
  control: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
): control is HTMLInputElement =>
  control.tagName === "INPUT" &&
  (control.type === "checkbox" || control.type === "radio");

const setNativeControlValue = (
  control: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
  value: string,
) => {
  const descriptor = Object.getOwnPropertyDescriptor(
    Object.getPrototypeOf(control),
    "value",
  );

  if (descriptor?.set) {
    descriptor.set.call(control, value);
    return;
  }

  control.value = value;
};

const preserveFormStateForConsentReload = (
  targetWindow: Window,
  targetDocument: Document,
) => {
  try {
    const controls = getEditableFormControls(targetDocument);
    const preservedControls = controls.map((control, index) => {
      if (isCheckableInput(control)) {
        return { checked: control.checked, index };
      }

      return { index, value: control.value };
    });
    const currentState =
      targetWindow.history.state &&
      typeof targetWindow.history.state === "object"
        ? targetWindow.history.state
        : {};

    targetWindow.history.replaceState(
      { ...currentState, __rhConsentFormState: preservedControls },
      "",
    );
  } catch {
    // The withdrawal must still stop the vendor when history is unavailable.
  }
};

export const restoreFormStateAfterConsentReload = (
  targetWindow: Window,
  targetDocument: Document,
) => {
  try {
    const currentState = targetWindow.history
      .state as ConsentReloadHistoryState | null;
    const preservedControls = currentState?.__rhConsentFormState;

    if (!preservedControls) {
      return;
    }

    const { __rhConsentFormState: _, ...restoredHistoryState } = currentState;
    targetWindow.history.replaceState(restoredHistoryState, "");

    targetWindow.setTimeout(() => {
      const controls = getEditableFormControls(targetDocument);

      preservedControls.forEach(({ checked, index, value }) => {
        const control = controls[index];

        if (!control) {
          return;
        }

        if (isCheckableInput(control)) {
          if (checked !== undefined) {
            control.checked = checked;
          }
        } else if (value !== undefined) {
          setNativeControlValue(control, value);
        }

        control.dispatchEvent(new Event("input", { bubbles: true }));
        control.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }, 0);
  } catch {
    // A failed restoration must not re-enable a withdrawn vendor.
  }
};

export const createMarketingVendorConsentController = (
  targetWindow: Window,
  targetDocument: Document,
  options: MarketingVendorConsentOptions,
) => {
  let hasLoaded = targetDocument.getElementById(options.scriptId) !== null;
  /**
   * Cookiebot fires the same answer several times (OnLoad, OnAccept and
   * OnConsentChanged can all carry one click, and the renewal dialog replays
   * events). Teardown is destructive — vendor cookies/session keys are wiped
   * and the page reloads once — so it must run per *transition*, never per
   * event: repeating an identical answer must be a no-op.
   */
  let lastHandledState: MarketingConsentState | null = null;

  const cookieDomains = (): string[] => {
    const hostname = targetWindow.location.hostname.replace(/^\./, "");

    return hostname.startsWith("www.")
      ? [hostname, hostname.slice("www.".length)]
      : [hostname];
  };

  const expireCookie = (name: string) => {
    const cookie = `${name}=; Max-Age=0; path=/`;
    targetDocument.cookie = cookie;

    cookieDomains().forEach((hostname) => {
      targetDocument.cookie = `${cookie}; domain=${hostname}`;
      targetDocument.cookie = `${cookie}; domain=.${hostname}`;
    });
  };

  const removeStorage = () => {
    const cookieNames = targetDocument.cookie
      .split(";")
      .map((cookie) => cookie.split("=", 1)[0]?.trim())
      .filter((name): name is string =>
        Boolean(name?.startsWith(options.cookiePrefix)),
      );

    new Set([...options.knownCookieNames, ...cookieNames]).forEach(
      expireCookie,
    );

    if (options.sessionStoragePrefix) {
      try {
        const storage = targetWindow.sessionStorage;
        Array.from({length: storage.length}, (_, index) => storage.key(index))
          .filter(
            (name): name is string =>
              name !== null &&
              name.startsWith(options.sessionStoragePrefix ?? ""),
          )
          .forEach((name) => targetWindow.sessionStorage.removeItem(name));
      } catch {
        // Storage access can be blocked independently of Cookiebot consent.
      }
    }

    if (options.localStoragePrefixes) {
      try {
        const storage = targetWindow.localStorage;
        Array.from({length: storage.length}, (_, index) => storage.key(index))
          .filter(
            (name): name is string =>
              name !== null &&
              Boolean(
                options.localStoragePrefixes?.some((prefix) =>
                  name.startsWith(prefix),
                ),
              ),
          )
          .forEach((name) => targetWindow.localStorage.removeItem(name));
      } catch {
        // A blocked localStorage getter must not prevent vendor teardown.
      }
    }
  };

  return {
    update(consentState: MarketingConsentState) {
      if (consentState === lastHandledState) {
        return;
      }

      lastHandledState = consentState;

      if (consentState === "granted") {
        if (!hasLoaded && !targetDocument.getElementById(options.scriptId)) {
          options.appendScript();
        }

        hasLoaded = true;
        return;
      }

      if (consentState !== "denied") {
        return;
      }

      removeStorage();

      if (hasLoaded) {
        targetDocument.getElementById(options.scriptId)?.remove();
        hasLoaded = false;

        if (!withdrawalReloadWindows.has(targetWindow)) {
          withdrawalReloadWindows.add(targetWindow);
          preserveFormStateForConsentReload(targetWindow, targetDocument);
          targetWindow.location.reload();
        }
      }
    },
  };
};

export const setupMarketingVendorConsentListeners = (
  targetWindow: Window,
  targetDocument: Document,
  getConsentState: () => MarketingConsentState,
  options: MarketingVendorConsentOptions,
) => {
  const controller = createMarketingVendorConsentController(
    targetWindow,
    targetDocument,
    options,
  );
  const updateConsent = () => controller.update(getConsentState());
  const consentEvents = [
    "CookiebotOnLoad",
    "CookiebotOnAccept",
    "CookiebotOnDecline",
    "CookiebotOnConsentChanged",
  ] as const;

  restoreFormStateAfterConsentReload(targetWindow, targetDocument);
  updateConsent();
  consentEvents.forEach((eventName) =>
    targetWindow.addEventListener(eventName, updateConsent),
  );

  return () => {
    consentEvents.forEach((eventName) =>
      targetWindow.removeEventListener(eventName, updateConsent),
    );
  };
};
